// src/mobile/screens/auth/LoginScreen.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginView from '../../../shared/screens/auth/LoginView';
import MobileScaleStage from '../../layout/MobileScaleStage';
import { userApi } from '../../../api/axios';
import { useI18n } from '../../../i18n/I18nProvider';
import { clearSignupAgent } from '../../../shared/utils/signupAgent';
import { resolveAuthErrorMessage } from '../../../shared/utils/systemMessage';
import { markBrowserAuthSession, shouldResumeStoredSessionFromLogin } from '../../../shared/utils/authPersistence';

export default function MobileLoginScreen() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [takeoverChallenge, setTakeoverChallenge] = useState(null);

  useEffect(() => {
    // 이미 로그인 토큰이 살아있는 상태로 /login이 직접 열리면
    // 로그인 화면에 머물지 않고 루트 복구 플로우로 보낸다.
    // 루트 진입 후 recoveryTables/currentTableCode/lastTableCode 기준으로 기존 테이블을 복구한다.
    try {
      const token = localStorage.getItem('token');
      if (token && shouldResumeStoredSessionFromLogin()) {
        window.location.replace('/?recovery=1');
      }
    } catch (_) {
      // localStorage 접근 실패 시 로그인 화면을 그대로 유지한다.
    }
  }, []);

  const handleSubmit = useCallback(async ({ username, password, autoLogin }) => {
    setError('');
    setLoading(true);

    try {
      const normalizedUsername = String(username || '').trim();
      const pendingTakeover = takeoverChallenge
        && takeoverChallenge.username === normalizedUsername
        && Number(takeoverChallenge.expiresAt || 0) > Date.now()
          ? takeoverChallenge
          : null;

      // 모바일은 1차 중복로그인 응답에서 받은 일회용 challenge를 같은 계정의 2차 시도에만 보낸다.
      // 일반 더블클릭이나 다른 계정 로그인에는 강제 인계가 발생하지 않는다.
      const res = await userApi.post('/login', {
        id: normalizedUsername,
        password,
        client_type: 'mobile',
        ...(pendingTakeover?.token ? { takeover_token: pendingTakeover.token } : {}),
      });
      const { token, user } = res.data || {};

      if (!token) {
        throw new Error('TOKEN_MISSING');
      }

      setTakeoverChallenge(null);
      localStorage.setItem('token', token);
      // ✅ 예전 테스트/구버전 잔여 키가 다른 계정 userCode로 fallback 되는 것을 차단
      localStorage.removeItem('user_code');
      localStorage.removeItem('userCode');
      if (user) {
        const uc = String(user.user_code || user.User_code || '').trim();
        const normalizedUser = { ...user, user_code: uc, User_code: uc };
        localStorage.setItem('user', JSON.stringify(normalizedUser));
      }

      // 현재 브라우저 세션은 새로고침 동안 유지하고, 자동로그인은 선택한 경우에만 영구 허용한다.
      markBrowserAuthSession({ autoLogin: !!autoLogin });

      // ✅ 로그인 후 앱 진입
      // /login에서 reload하면 로그인 화면에 머물 수 있으므로 루트로 이동한다.
      // 루트 진입 시 recoveryTables/lastTableCode 복구 로직이 기존 테이블을 다시 연다.
      window.location.replace('/?recovery=1');
    } catch (err) {
      const data = err?.response?.data || {};
      const normalizedUsername = String(username || '').trim();
      if (String(data?.error || '').toUpperCase() === 'ALREADY_LOGGED_IN' && data?.takeover_required && data?.takeover_token) {
        const ttlSec = Math.max(1, Number(data?.takeover_expires_in_sec || 60));
        setTakeoverChallenge({
          username: normalizedUsername,
          token: String(data.takeover_token),
          expiresAt: Date.now() + ttlSec * 1000,
        });
        // 기존 번역 키를 유지하면서 2차 로그인 동작만 추가 안내한다. 번역 키가 없는 언어는 fallback을 사용한다.
        setError(t(
          'auth.login.error.alreadyLoggedInTakeover',
          null,
          '이미 접속 중인 계정입니다. 같은 계정으로 한 번 더 로그인하면 기존 접속을 종료하고 로그인합니다.'
        ));
      } else {
        if (takeoverChallenge && takeoverChallenge.username !== normalizedUsername) setTakeoverChallenge(null);
        const msg = err?.message === 'TOKEN_MISSING'
          ? t('auth.login.error.tokenMissing', null, '로그인 응답에 토큰이 없습니다.')
          : resolveAuthErrorMessage(t, data || err, t('auth.login.error.failed', null, '로그인에 실패했습니다.'));
        setError(msg);
      }
      // eslint-disable-next-line no-console
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  }, [t, takeoverChallenge]);

  const handleAccountRecovery = useCallback(() => {
    navigate('/account-recovery');
  }, [navigate]);

  const handleCreateAccount = useCallback(() => {
    clearSignupAgent();
    navigate('/signup/policy');
  }, [navigate]);

  return (
    <>
      <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none', background: '#000' }}>
        <video autoPlay loop muted playsInline preload="metadata" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}>
          <source src="/assets/test.mp4" type="video/mp4" />
        </video>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 52% 42%, rgba(255,255,255,0.16), transparent 22%), linear-gradient(180deg, rgba(0,0,0,0.16) 0%, rgba(0,0,0,0.32) 52%, rgba(0,0,0,0.72) 100%), linear-gradient(90deg, rgba(0,0,0,0.54) 0%, rgba(0,0,0,0.06) 45%, rgba(0,0,0,0.58) 100%), rgba(0,0,0,0.14)' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(0,0,0,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.16) 1px, transparent 1px)', backgroundSize: '3px 3px', opacity: 0.45, mixBlendMode: 'multiply' }} />
      </div>
      {error && (
        <div
          style={{
            position: 'fixed',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            padding: '10px 14px',
            background: 'rgba(0,0,0,0.7)',
            border: '1px solid #ff6b6b',
            color: '#ff6b6b',
            fontSize: 15,
            maxWidth: 320,
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      )}

      <MobileScaleStage baseWidth={375} baseHeight={812} fit="contain" verticalAlign="top" background="transparent">
        <LoginView
          renderVideoBackground={false}
          onSubmit={handleSubmit}
          onAccountRecovery={handleAccountRecovery}
          onCreateAccount={handleCreateAccount}
          loading={loading}
          useMobileInputModal
          mobileFontBoost={1}
        />
      </MobileScaleStage>
    </>
  );
}
